import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useAuth } from "../core/AuthContext";
import { useSemester } from "../core/SemesterContext";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Icon } from "@iconify/react";
import NavHeader from "./NavHeader";

import DateTimePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from 'react-datepicker';
import { enGB } from 'date-fns/locale';
import "../styles/Form.css";
import "../styles/responsive.css";
import "../styles/edit.css";
import "../styles/OpenCall.css";
import useUsers from "./hooks/useUsers";
import CustomAlert from "./CustomAlert";
import CustomAlertWithButtons from "./CustomAlertWithButtons";

import OpenCallService from "../core/services/OpenCallService";

import { URL } from '../core/config';
import {
  canCreateOpenCall,
  canManageOpenCalls,
  canVoteOpenCall,
  isEventOrganizer,
} from '../core/utils/permissionUtils';

const usersURL = URL + "/users";
const eventsURL = URL + "/events";
const openCallURL = URL + "/opencall";

// Register the locale
registerLocale('en-GB', enGB);

function OpenCall() {

  const history = useHistory();

  const { loading } = useAuth();
  const { currentSemester } = useSemester();
  const authContext = useAuth();
  const contextUser = authContext.user;
  const { eventId } = useParams(); // Get event ID from URL params
  const [eventData, setEventData] = useState({});
  const [openCallData, setOpenCallData] = useState({});
  const [userMap, setUserMap] = useState({})
  // const [reopenCall, setReopenCall] = useState(false);
  const [notifyDeadlineExtension, setNotifyDeadlineExtension] = useState(true);


  const [openCallFormat, setOpenCallFormat] = useState(
    eventData.openCallSpread ? "spread" : "shot"
  );

  // For CustomAlert
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    type: '',
    message: ''
  });

  const [alertActionInfo, setAlertActionInfo] = useState({
    show: false,
    title: '',
    type: '',
    message: '',
    btn1Action: () => { },
    btn2Action: () => { },
    btn1Text: '',
    btn2Text: ''
  });

  // Use the useUsers hook instead of fetching users directly
  const { formatUserDisplayName } = useUsers();

  // Function to add an icon to error feedback below the input fields
  const withIcon = (message) => (
    <span style={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
      <Icon icon="f7:exclamationmark-circle-fill" style={{ marginRight: '5px', marginLeft: '24px' }} /> {message}
    </span>
  );

  // Helper function to create openCallPayload
  const createOpenCallPayload = (values, finalDate, shouldNotifyExtension = false) => {
    return {
      openCallName: eventData.name,
      event: eventData,
      openCallDeadline: finalDate || eventData.date,
      votingDeadline: openCallFormat === "shot" ? values.votingDeadline : undefined,
      numberSpots: openCallFormat === "shot" ? values.spots : undefined,
      openCallSpread: openCallFormat === "spread" ? values.openCallSpread : undefined,
      mos: eventData.mos || [],
      vmos: eventData.vmos || [],
      comment: values.comment,
      type: eventData.type,
      level: eventData.level,
      semester: currentSemester,
      notifyDeadlineExtension: shouldNotifyExtension
    };
  };

  // Helper function to get user name by ID using cached users
  const getUserNameById = useCallback((userId, users) => {
    const user = users.find((user) => user._id === userId);
    return user ? formatUserDisplayName(user) : "Unknown User";
  }, [formatUserDisplayName]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {

        // Fetch event
        const eventData = await axios.get(eventsURL + `/${eventId}`);
        setEventData(eventData.data);

        // Fetch OpenCall
        try {
          const openCallData = await axios.get(openCallURL + `/from_event/${eventId}`);
          setOpenCallData(openCallData.data);
          if (eventData.data.openCallSpread || openCallData.data.openCallSpread) {
            setOpenCallFormat("spread");
          } else {
            setOpenCallFormat("shot");
          }
        } catch (openCallError) {
          // If no openCallData is found (Error 404)
          // then we just use the eventData values for this page
          if (openCallError.response && openCallError.response.status === 404) {
            //console.log("No open call found for this event")
            setOpenCallData({});
          }
        }

        // Extract MO and VMO IDs from event data
        const userIdsToFetch = [];
        if (eventData.data.mos && Array.isArray(eventData.data.mos)) {
          userIdsToFetch.push(...eventData.data.mos);
        }

        if (eventData.data.vmos && Array.isArray(eventData.data.vmos)) {
          userIdsToFetch.push(...eventData.data.vmos);
        }

        // Only fetch users if we have IDs to fetch
        if (userIdsToFetch.length > 0) {
          // Remove duplicates
          const uniqueUserIds = [...new Set(userIdsToFetch)];

          // Fetch each user individually using the existing route
          const userPromises = uniqueUserIds.map(userId =>
            axios.get(`${usersURL}/${userId}`)
              .then(response => response.data)
              .catch(error => {
                console.error(`Error fetching user ${userId}:`, error);
                return null; // Return null for failed requests
              })
          );

          const users = await Promise.all(userPromises);

          // Create map <user id, display name>
          const userMap = {};
          users.forEach(user => {
            if (user && user._id) { // Check if user exists (not null from error)
              userMap[user._id] = getUserNameById(user._id, users);
            }
          });

          setUserMap(userMap);
        } else {
          //console.log('No MO or VMOs found, skipping user fetch');
          setUserMap({});
        }

      } catch (err) {
        //console.log(err);
      }
    };
    fetchEvents();
  }, [eventId, eventData.openCallSpread, getUserNameById]);

  const canManageThisOpenCall = canManageOpenCalls(contextUser, eventData);
  const canMaintainThisOpenCall =
    canCreateOpenCall(contextUser) || canManageThisOpenCall;
  const canCreateThisOpenCall =
    canCreateOpenCall(contextUser) || isEventOrganizer(contextUser, eventData);

  function canAccessOpenCallPage() {
    return canCreateOpenCall(contextUser) || canManageThisOpenCall;
  }

  // Check if user has permission to view this page
  if (!canAccessOpenCallPage()) {
    return (
      <div>
        <div>You do not have permission to view this page.</div>
        <div>
          <p>Your details:</p>
          <ul>
            <li>Email: {contextUser.email || 'Email not available'}</li>
            <li>Name: {contextUser.name || 'Name not available'}</li>
            <li>Login active: {contextUser.loginActive === true ? 'Yes' : 'No'}</li>
          </ul>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div>Loading...</div>; // Show loading until the initial values are set
  }

  // Handle the Discard Changes click
  const handleDiscardChanges = () => {
    // Show confirmation alert with CustomAlertWithButtons
    setAlertActionInfo({
      show: true,
      title: 'Do you want to discard all the changes?',
      type: 'error',
      message: 'Are you sure you want to discard all the changes made? Any unsaved changes will be lost.',
      btn1Action: () => {
        // Just close the alert without navigating
        setAlertActionInfo((prevState) => ({
          ...prevState,
          show: false,
        }));
      },
      btn2Action: () => {
        // Navigate to the events list page if confirmed
        history.push('/events-list');
        // Close the alert after navigation
        setAlertActionInfo((prevState) => ({
          ...prevState,
          show: false,
        }));
      },
      btn1Text: 'No, I\'ll keep editing',
      btn2Text: 'Yes, discard',
    });
  };

  const handleDelete = () => {
    // Configure the CustomAlertWithButtons to ask for confirmation
    setAlertActionInfo({
      show: true,
      title: 'Do you want to delete this Open Call permanently?',
      type: 'error',
      message: 'This action will delete all the information related to this Open Call. Are you sure you want to proceed? This cannot be undone.',
      btn2Action: async () => {
        try {
          // Proceed with the deletion logic

          axios.delete(`${URL}/opencall/${openCallData._id}`)
            .then(response => {
              // alert("Open call deleted successfully!");
              setAlertInfo({
                show: true,
                type: 'success',
                message: 'Open Call deleted successfully!',
              });
              history.push("/events-list");
              //window.location.href = "/events-list";
            })
            .catch(error => {
              console.error("Error deleting open call:", error);
              //alert("Failed to delete open call. Please try again.");
              setAlertInfo({
                show: true,
                type: 'error',
                message: `Failed to delete Open Call. Please try again.`,
              });
            });
        } catch (error) {

          setAlertInfo({
            show: true,
            type: 'error',
            message: `Failed to delete Open Call. Please try again.`,
          });

        } finally {
          // Close the confirmation alert after an action (success or error)
          setAlertActionInfo((prevState) => ({
            ...prevState,
            show: false,
          }));
        }
      },
      btn1Action: () => {
        // Dismiss the alert (do nothing)
        setAlertActionInfo((prevState) => ({
          ...prevState,
          show: false,
        }));
      },
      btn1Text: 'No, not now',
      btn2Text: 'Yes, delete',
    });
  };

  const handleCancel = () => {
    // Configure the CustomAlertWithButtons to ask for confirmation
    setAlertActionInfo({
      show: true,
      title: 'Do you want to cancel this Open Call?',
      type: 'error',
      message: 'This action will set the Open Call status to "Cancelled", and volunteers will no longer be able to apply. Are you sure you want to proceed?',
      btn2Action: async () => {
        try {
          // Use the OpenCallService to cancel the open call
          await OpenCallService.cancelOC(openCallData._id);

          setAlertInfo({
            show: true,
            type: 'success',
            message: 'Open Call cancelled successfully!',
          });

          // Redirect after a short delay
          setTimeout(() => {
            history.push("/events-list");
          }, 1500);

        } catch (error) {
          console.error(error);
          setAlertInfo({
            show: true,
            type: 'error',
            message: 'Failed to cancel the Open Call. Please try again.',
          });
        } finally {
          // Close the confirmation alert
          setAlertActionInfo((prevState) => ({
            ...prevState,
            show: false,
          }));
        }
      },
      btn1Action: () => {
        // Dismiss the alert
        setAlertActionInfo((prevState) => ({
          ...prevState,
          show: false,
        }));
      },
      btn1Text: 'No, keep it open',
      btn2Text: 'Yes, cancel',
    });
  };

  const handleReopenCall = (currentDeadline, formValues) => {
    // Validate that the deadline is in the future
    if (openCallData._id && currentDeadline <= new Date()) {
      setAlertInfo({
        show: true,
        type: 'error',
        message: 'The Open Call deadline has already passed. You cannot reopen an Open Call with an expired deadline.',
      });
      return;
    }
    setAlertActionInfo({
      show: true,
      title: 'Reopen this Open Call?',
      type: 'warning',
      message: 'The Open Call will be reopened and all ESNers will be notified via email.',
      btn1Action: () => {
        // Dismiss without reopening
        setAlertActionInfo((prevState) => ({ ...prevState, show: false }));
      },
      btn2Action: async () => {
        try {
          const openCallPayload = createOpenCallPayload(formValues, currentDeadline);

          await OpenCallService.openOC(openCallPayload);

          setAlertInfo({
            show: true,
            type: 'success',
            message: 'Open Call reopened successfully!',
          });

          // Redirect after a short delay
          setTimeout(() => {
            history.push("/events-list");
          }, 1500);

        } catch (error) {
          console.error(error);
          setAlertInfo({
            show: true,
            type: 'error',
            message: 'Failed to reopen the Open Call. Please try again.',
          });
        } finally {
          // Close the confirmation alert
          setAlertActionInfo((prevState) => ({
            ...prevState,
            show: false,
          }));
        }
      },
      btn1Text: 'Cancel',
      btn2Text: 'Yes, reopen and notify',
    });
  };


  return (
    <div className="page-container" style={{ marginBottom: '50px' }}>
      <NavHeader />

      <h1 className="page-title">Open Call: {eventData.name}</h1>

      {openCallData && openCallData.status === 'Cancelled' && (
        <div style={{
          padding: '10px 15px',
          margin: '15px auto',
          border: '1px solid transparent',
          borderRadius: '4px',
          textAlign: 'center',
          fontWeight: 'bold',
          backgroundColor: '#f2dede',
          borderColor: '#ebccd1',
          color: '#a94442',
          width: 'fit-content'
        }}>
          Open Call Cancelled
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>

        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
          <label>MOs</label>
          <div className="edit-input">
            {eventData.mos && eventData.mos.length > 0 ? eventData.mos.map(mo => userMap[mo]).join(', ') : 'No MOs'}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
          <label>VMOs</label>
          <div className="edit-input">
            {eventData.vmos && eventData.vmos.length > 0 ? eventData.vmos.map(vmo => userMap[vmo]).join(', ') : 'No VMOs'}
          </div>
        </div>
      </div>

      <div className="open-call-format-toggle">
        <button
          type="button"
          className={`open-call-format-toggle-btn ${openCallFormat === "shot" ? "active" : ""}`}
          onClick={() => setOpenCallFormat("shot")}
        >
          <Icon icon="nimbus:shot" className='icon' style={{ marginRight: 8, width: 18, height: 18 }} /> Shot
        </button>
        <button
          type="button"
          className={`open-call-format-toggle-btn ${openCallFormat === "spread" ? "active" : ""}`}
          onClick={() => setOpenCallFormat("spread")}
        >
          <Icon icon="garden:file-spreadsheet-fill-12" style={{ marginRight: 8, width: 14, height: 14 }} /> Spread
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>

        <Formik
          initialValues={{
            name: eventData.name || '',
            date: openCallFormat === "spread"
              ? eventData.date
                ? new Date(new Date(eventData.date).getTime() + 24 * 60 * 60 * 1000)
                : null
              : openCallData.openCallDeadline
                ? new Date(openCallData.openCallDeadline)
                : null,
            time: openCallFormat === "spread"
              ? eventData.date
                ? new Date(new Date(eventData.date).getTime() + 24 * 60 * 60 * 1000)
                : null
              : openCallData.openCallDeadline
                ? new Date(openCallData.openCallDeadline)
                : null,
            votingDate: openCallFormat === "shot" && openCallData.votingDeadline
              ? new Date(openCallData.votingDeadline)
              : null,
            votingTime: openCallFormat === "shot" && openCallData.votingDeadline
              ? new Date(openCallData.votingDeadline)
              : null,
            votingDeadline: openCallFormat === "shot" && openCallData.votingDeadline
              ? new Date(openCallData.votingDeadline)
              : null,
            spots: openCallFormat === "shot" ? openCallData.numberSpots || eventData.spots || ''
              : '',
            comment: openCallData.comment || eventData.comment || '',
            openCallSpread: eventData.openCallSpread || openCallData.openCallSpread || '',
            meetingPoint: eventData.meetingPoint || '',
            mos: eventData.mos || [],
            vmos: eventData.vmos || [],
            participants: eventData.participants || []
          }}
          enableReinitialize={true}
          validationSchema={Yup.object().shape({
            name: Yup.string(),
            date: Yup.date().nullable().required(withIcon('Deadline is required')),
            time: Yup.string().nullable().required(withIcon('Time is required')),
            spots: openCallFormat === "shot"
              ? Yup.number().required(withIcon('Number of spots is required')).positive('Must be a positive number').integer('Must be a whole number')
              : Yup.mixed().notRequired(),
            openCallSpread: openCallFormat === "spread"
              ? Yup.string().required(withIcon('Open Call Spread link is required'))
              : Yup.mixed().notRequired(),
            comment: Yup.string().required(withIcon('Comment is required')),
            mos: Yup.array().of(Yup.string()).nullable(),
            vmos: Yup.array().of(Yup.string()).nullable(),
            participants: Yup.array().of(Yup.string()).nullable(),
          })}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            let finalDate;
            if (openCallFormat === "spread") {
              finalDate = eventData.date
                ? new Date(new Date(eventData.date).getTime() + 24 * 60 * 60 * 1000)
                : null;
            } else {
              finalDate = values.date;
              if (values.time) {
                finalDate = new Date(values.date);
                // Only hours and minutes are set here on purpose: seconds and
                // milliseconds are inherited from values.date, which is seeded
                // from the stored openCallDeadline. The server decides whether
                // to send the "deadline changed" email by comparing the old and
                // new deadlines with an exact getTime() match, so an untouched
                // deadline has to round-trip to the identical timestamp.
                // Passing 0, 0 for seconds/ms here would make every edit look
                // like a deadline change and re-send the email (issue #302).
                finalDate.setHours(values.time.getHours(), values.time.getMinutes());
              }
            }

            let votingDeadline = "Not set";

            if (
              openCallFormat === "shot" &&
              canVoteOpenCall(contextUser) &&
              values.votingDate &&
              values.votingTime
            ) {
              votingDeadline = new Date(values.votingDate);
              votingDeadline.setHours(
                values.votingTime.getHours(),
                values.votingTime.getMinutes(),
                0,
                0
              );
            }

            // Only notify volunteers when the deadline actually changed (earlier or later)
            const deadlineChanged = openCallData._id &&
              openCallData.openCallDeadline &&
              finalDate &&
              new Date(openCallData.openCallDeadline).getTime() !== finalDate.getTime();

            const shouldNotifyDeadlineChange = deadlineChanged &&
              notifyDeadlineExtension &&
              openCallData.status === 'Open';

            const openCallPayload = createOpenCallPayload(values, finalDate, shouldNotifyDeadlineChange);

            if (!openCallData._id) {
              if (!canCreateThisOpenCall) {
                setAlertInfo({
                  show: true,
                  type: 'error',
                  message: 'Creating an open call needs the Create Open Call permission.'
                });
                setSubmitting(false);
                return;
              }
              // Send POST request to create open call
              axios.post(`${URL}/opencall`, openCallPayload)
                .then(response => {
                  //alert("Open call created successfully!");
                  resetForm();
                  setAlertInfo({
                    show: true,
                    type: 'success',
                    message: 'This Open Call was created successfully!'
                  });

                  setTimeout(() => {
                    history.push("/opencalls-list");
                    //window.location.href = "/opencall";
                  }, 1500);
                })
                .catch(error => {
                  console.error("Error creating open call:", error);
                  //alert("Failed to create open call. Please try again.");
                  setAlertInfo({
                    show: true,
                    type: 'error',
                    message: 'Failed to create open call. Please try again.'
                  });
                })
                .finally(() => {
                  setSubmitting(false);
                });
            } else { // Send POST request to edit the open call
              // if (reopenCall) {
              //   OpenCallService.openOC(openCallData._id);
              // }
              axios.put(`${URL}/opencall/edit/${openCallData._id}`, openCallPayload)
                .then(response => {
                  //alert("Open call edited successfully!");
                  resetForm();
                  //window.location.href = "/events-list";

                  let successMessage = 'The Open Call was successfully updated without notifying volunteers.';
                  if (deadlineChanged && shouldNotifyDeadlineChange) {
                    successMessage = 'The Open Call was successfully updated and volunteers have been informed about the deadline change.';
                  }

                  setAlertInfo({
                    show: true,
                    type: 'success',
                    message: successMessage
                  });

                  // Reset the checkbox after successful submission
                  setNotifyDeadlineExtension(false);

                  setTimeout(() => {
                    history.push("/opencalls-list");
                  }, 1500);
                })
                .catch(error => {
                  console.error("Error editing open call:", error);
                  // alert("Failed to edit open call. Please try again.");
                  setAlertInfo({
                    show: true,
                    type: 'error',
                    message: 'The changes couldn\'t be saved. Please try again.'
                  });
                })
                .finally(() => {
                  setSubmitting(false);
                });
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
            <Form onSubmit={handleSubmit} >
              <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
                <label htmlFor="openCallDeadline">Open Call Deadline</label>
                <div className="date-time-container">
                  <DateTimePicker
                    selected={values.date}
                    onChange={(date) => setFieldValue('date', date)}
                    showTimeSelect={false}
                    dateFormat="dd/MM/yyyy"
                    locale="en-GB"
                    placeholderText={new Date().toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                    className={`${touched.date && errors.date ? 'is-invalid' : ''}`}
                    wrapperClassName="custom-datepicker-wrapper"
                    name="date"
                  />

                  <DateTimePicker
                    selected={values.time}
                    onChange={(time) => {
                      setFieldValue('time', time);
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={5}
                    timeCaption="Time"
                    dateFormat="HH:mm"
                    locale="en-GB"
                    placeholderText="00:00"
                    className={`${touched.date && errors.date ? 'is-invalid' : ''}`}
                    wrapperClassName="custom-timepicker-wrapper"
                  />
                </div>
                {touched.date && errors.date && <div className="invalid-feedback">{errors.date}</div>}
                {touched.time && errors.time && <div className="invalid-feedback">{errors.time}</div>}
                {openCallFormat === "spread" && (
                  <small style={{ color: "#000", paddingLeft: "16px" }}>
                    Deadline for spreadsheets is automatically set to the date of the event plus one day. Change if needed.
                  </small>)}
              {openCallFormat === "shot" && canVoteOpenCall(contextUser) &&(
                <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
                <label htmlFor="votingDeadline">Voting Deadline</label>
                <div className="date-time-container">
                  {/* Date Picker */}
                  <DateTimePicker
                    selected={values.votingDate}
                    onChange={(date) => {
                      setFieldValue('votingDate', date);

                      if (date && values.votingTime) {
                        const deadline = new Date(date);
                        deadline.setHours(
                          values.votingTime.getHours(),
                          values.votingTime.getMinutes(),
                          0,
                          0
                        );
                        setFieldValue('votingDeadline', deadline, false);
                      }
                    }}
                    showTimeSelect={false}
                    dateFormat="dd/MM/yyyy"
                    locale="en-GB"
                    placeholderText={new Date().toLocaleDateString('en-GB')}
                    className={`${touched.votingDate && errors.votingDate ? 'is-invalid' : ''}`}
                    wrapperClassName="custom-datepicker-wrapper"
                    name="votingDate"
                  />

                  {/* Time Picker */}
                  <DateTimePicker
                    selected={values.votingTime}
                    onChange={(time) => {
                      setFieldValue('votingTime', time);

                      if (values.votingDate && time) {
                        const deadline = new Date(values.votingDate);
                        deadline.setHours(
                          time.getHours(),
                          time.getMinutes(),
                          0,
                          0
                        );
                        setFieldValue('votingDeadline', deadline);
                      }
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={5}
                    timeCaption="Time"
                    dateFormat="HH:mm"
                    locale="en-GB"
                    placeholderText="00:00"
                    className={`${touched.votingTime && errors.votingTime ? 'is-invalid' : ''}`}
                    wrapperClassName="custom-timepicker-wrapper"
                    name="votingTime"
                  />

                </div>
                {touched.votingDate && errors.votingDate && (
                  <div className="invalid-feedback">{errors.votingDate}</div>
                )}
                {touched.votingTime && errors.votingTime && (
                  <div className="invalid-feedback">{errors.votingTime}</div>
                )}
              </div>
              )}

                {/* Notify volunteers when the deadline is changed */}
                {openCallData._id && openCallData.status === 'Open' && (
                  <div className="form-check" style={{ marginTop: '15px', paddingLeft: '16px' }}>
                    <input
                      type="checkbox"
                      id="notifyDeadlineExtension"
                      name="notifyDeadlineExtension"
                      checked={notifyDeadlineExtension}
                      onChange={(e) => setNotifyDeadlineExtension(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    <label htmlFor="notifyDeadlineExtension" style={{ fontSize: '14px', color: '#000' }}>
                      Notify volunteers via email if the deadline is changed
                    </label>
                  </div>
                )}
              </div>

              {/* shot Format Fields */}
              {openCallFormat === "shot" && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
                    <label>Number of Spots</label>
                    <input
                      type="number"
                      name="spots"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.spots}
                      placeholder="0"
                      className={touched.spots && errors.spots ? "edit-input error-input" : "edit-input"}
                      step="1"
                    />
                    {touched.spots && errors.spots &&
                      <div className="error-message">{errors.spots}</div>
                    }
                  </div>
                </>
              )}

              {/* Spread Format Fields */}
              {openCallFormat === "spread" && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
                    <label>Open Call Spread</label>
                    <input
                      type="text"
                      name="openCallSpread"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.openCallSpread}
                      placeholder="Paste the Open Call Spread link here"
                      className={touched.openCallSpread && errors.openCallSpread ? "edit-input error-input" : "edit-input"}
                    />
                    {touched.openCallSpread && errors.openCallSpread &&
                      <div className="error-message">{errors.openCallSpread}</div>
                    }
                  </div>
                </>
              )}


              <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
                <label>Comment</label>
                <textarea
                  name="comment"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.comment}
                  placeholder="Add information like: place of the event, date, name of the OC and more..."
                  className={touched.comment && errors.comment ? "comment-input error-input" : "comment-input"}
                  style={{
                    minHeight: '100px',  // Ensures a good starting height
                    width: '100%',
                    resize: 'vertical',  // Allows resizing the box
                    padding: '8px',
                    fontSize: '14px',
                  }}
                />

                {touched.comment && errors.comment &&
                  <div className="error-message">{errors.comment}</div>
                }
              </div>

              {/* This protected for now, only for heads or higher level
              {openCallData && ['Closed', 'On Voting', 'Cancelled'].includes(openCallData.status) && (
                <div className="form-check-reopening">
                  <input
                    type="checkbox"
                    id="reopenCall"
                    name="reopenCall"
                    checked={reopenCall}
                    onChange={(e) => handleReopenCall(e.target.checked)}
                    onBlur={handleBlur}
                  />
                  <label htmlFor="reopenCall"> Reopen Open Call after Saving </label>
                </div>
              )} */}


              <div style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignContent: 'center', marginTop: '20px' }}>

                {canMaintainThisOpenCall && openCallData && openCallData._id && openCallData.status !== 'Cancelled' && (
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="button button-danger"
                    style={{ width: '200px', margin: '10px' }}
                  >
                    Cancel Open Call
                  </button>
                )}

                {canMaintainThisOpenCall && openCallData && openCallData._id && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="button button-danger"
                    style={{ width: '200px', margin: '10px' }}
                  >
                    Delete Open Call
                  </button>
                )}
                <button type="button" className="button button-transparent-black" style={{ width: '200px', margin: '10px' }} onClick={handleDiscardChanges}>{openCallData._id ? "Discard Changes" : "Discard Open Call"}</button>
                {/* Delete button - only show if open call exists */}
                {canMaintainThisOpenCall && openCallData && openCallData._id && ['Closed', 'On Voting', 'Cancelled'].includes(openCallData.status) && (
                  <button type="button" className="button button-transparent-black" style={{ width: '200px', margin: '10px' }} onClick={
                    () => {
                      // Calculate current deadline from form values
                      let currentDeadline;
                      if (openCallFormat === "spread") {
                        currentDeadline = eventData.date
                          ? new Date(new Date(eventData.date).getTime() + 24 * 60 * 60 * 1000)
                          : null;
                      } else {
                        currentDeadline = values.date;
                        if (values.time) {
                          currentDeadline = new Date(values.date);
                          currentDeadline.setHours(values.time.getHours(), values.time.getMinutes());
                        }
                      }
                      handleReopenCall(currentDeadline, values);
                    }}
                  >
                    Reopen Open Call
                  </button>
                )}

                {(openCallData._id || canCreateThisOpenCall) && (
                <button type="submit" disabled={isSubmitting} className="button" style={{ width: '200px', margin: '10px' }} onClick={() => {
                  // Trigger a warning if any required field is empty
                  if (!values.date || !values.time || !values.comment || (openCallFormat === "shot" && !values.spots && !values.openCallDeadline ) || (openCallFormat === "spread" && !values.openCallSpread)) {
                    setAlertInfo({
                      show: true,
                      type: 'warning',
                      message: 'Please fill in all the required fields to save the Open Call'
                    });
                  }
                }}>
                  {openCallData._id ? "Save Changes" : "Create Open Call"}
                </button>
                )}
                {!openCallData._id && !canCreateThisOpenCall && (
                  <p style={{ width: '100%', textAlign: 'center', margin: '10px' }}>
                    Creating an open call needs the Create Open Call permission.
                  </p>
                )}

              </div>
            </Form>
          )}
        </Formik>
      </div>
      {/* Alert that shows upon event submission */}
      {alertInfo.show && (
        <CustomAlert
          type={alertInfo.type}
          message={alertInfo.message}
          autoDismiss={true}
          timeout={4000}
          onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
        />
      )}

      {alertActionInfo.show && (
        <CustomAlertWithButtons
          title={alertActionInfo.title}
          type={alertActionInfo.type}
          message={alertActionInfo.message}
          onButton1Click={alertActionInfo.btn1Action}
          onButton2Click={alertActionInfo.btn2Action}
          button1Text={alertActionInfo.btn1Text}
          button2Text={alertActionInfo.btn2Text}
        />)}
    </div>
  );
}

export default OpenCall;
